import { computed, readonly, ref, shallowRef, toRaw } from 'vue'
import type { DeepPartial, LocaleCode, SiteConfig } from '../types/site'
import { rewriteMedia } from '../utils/media'

const config = shallowRef<SiteConfig | null>(null)
const locale = ref<LocaleCode>('zh')
const loading = ref(false)
const error = ref<string | null>(null)

function clone<T>(value: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value)) as T
}

function mergeDeep<T extends object>(target: T, patch: DeepPartial<T>): T {
  const output = clone(target) as Record<string, unknown>
  for (const [key, value] of Object.entries(patch)) {
    const current = output[key]
    if (
      value !== null
      && typeof value === 'object'
      && !Array.isArray(value)
      && current !== null
      && typeof current === 'object'
      && !Array.isArray(current)
    ) {
      output[key] = mergeDeep(current as object, value as DeepPartial<object>)
    } else if (value !== undefined) {
      output[key] = clone(value)
    }
  }
  return output as T
}

function runtimeConfigUrl(): string {
  return new URL('content/site.json', document.baseURI).toString()
}

function readEmbeddedConfig(): SiteConfig | null {
  const element = document.querySelector<HTMLScriptElement>('#openrare-embedded-config')
  if (!element?.textContent) return null
  try {
    return JSON.parse(element.textContent) as SiteConfig
  } catch {
    return null
  }
}

async function loadConfig(url?: string): Promise<SiteConfig> {
  loading.value = true
  error.value = null
  try {
    const embedded = !url ? readEmbeddedConfig() : null
    let next: SiteConfig
    if (embedded) {
      next = embedded
    } else {
      const response = await fetch(url ?? runtimeConfigUrl(), { cache: 'no-store' })
      if (!response.ok) throw new Error(`配置加载失败：HTTP ${response.status}`)
      next = await response.json() as SiteConfig
    }
    // ./media/ 素材换成最终地址（CDN 或本服务器，见 utils/media.ts）
    next = rewriteMedia(next)
    config.value = next
    if (!next.copy[locale.value]) locale.value = next.defaultLocale
    return next
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : String(reason)
    throw reason
  } finally {
    loading.value = false
  }
}

/**
 * 拉后台改过的运行时覆盖（跳转链接、助手开关）合并进配置。
 * 拉不到就静默跳过，页面照常用 site.json 里的值。
 */
async function loadOverrides(): Promise<void> {
  if (!config.value || typeof window === 'undefined' || window.location.protocol === 'file:') return
  if (config.value.analytics?.overrides === false) return
  const url = config.value.analytics?.overridesEndpoint || '/assistant/api/site-overrides'
  try {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), 2500)
    const response = await fetch(url, { cache: 'no-store', signal: controller.signal })
    window.clearTimeout(timer)
    if (!response.ok) return
    const data = await response.json() as { patch?: DeepPartial<SiteConfig> }
    if (data?.patch && Object.keys(data.patch).length) setConfig(data.patch)
  } catch {
    // 助手服务没起或网络问题：不影响页面
  }
}

function setConfig(patch: DeepPartial<SiteConfig>): SiteConfig | null {
  if (!config.value) return null
  config.value = rewriteMedia(mergeDeep(toRaw(config.value), patch))
  return config.value
}

function setLocale(nextLocale: LocaleCode): void {
  if (!config.value?.copy[nextLocale]) return
  locale.value = nextLocale
  const language = config.value.languages.find((item) => item.code === nextLocale)
  document.documentElement.lang = language?.documentLanguage ?? nextLocale
  localStorage.setItem('openrare-welcome-locale', nextLocale)
}

function installRuntimeApi(): void {
  window.OpenRareWelcome = {
    getConfig: () => config.value ? clone(toRaw(config.value)) : null,
    setConfig,
    loadConfig,
    getLocale: () => locale.value,
    setLocale,
  }
}

export function useSiteConfig() {
  const copy = computed(() => {
    if (!config.value) return null
    return config.value.copy[locale.value] ?? config.value.copy[config.value.defaultLocale]
  })

  return {
    config,
    locale,
    copy,
    loading: readonly(loading),
    error: readonly(error),
    loadConfig,
    loadOverrides,
    setConfig,
    setLocale,
    installRuntimeApi,
  }
}

export function restoreLocale(): void {
  const saved = localStorage.getItem('openrare-welcome-locale')
  if (saved) locale.value = saved
}
